'use client'

import type { UseCookiebotConsentGateType } from '@maestro/cookiebot-cookie-consent/components/cookiebot-consent-gate/cookiebot-consent-gate.types'
import type { Subscription } from 'rxjs'

import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useId, useRef, useState } from 'react'

import { CookiebotTrigger } from '@maestro/cookiebot-cookie-consent/components/cookiebot-trigger'
import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import {
  ConsentGateProps,
  EventPayloadType,
  EventType,
} from '@maestro/data-layer'
import { theme } from '@maestro/local/theme'

export const useCookiebotConsentGate = (
  props: ConsentGateProps
): UseCookiebotConsentGateType => {
  const { contentType, consentCategory = 'all' } = props
  const t = useTranslations('cookiebot-cookie-consent')
  const id = useId()
  const { subscribeTo } = useEventBusContext()
  const subscriptionsRef = useRef<Subscription[]>([])
  const [ready, setReady] = useState(false)
  const [hasConsent, setHasConsent] = useState(false)

  const { consentGateEnabled, consents } = theme().site.consentConfig

  const cookieType = t(`cookie-types.${consentCategory}`)
  const text = t.rich(`cookiebot-consent-gate.${contentType}`, {
    type: cookieType,
    link: (chunk) => <CookiebotTrigger customLabel={chunk} />,
  })

  const checkConsent = useCallback(
    (payload: EventPayloadType[EventType.COOKIE_CONSENT_SET]) => {
      const requiredConsents = consents[consentCategory]
        ?.split(',')
        .filter((category) => {
          return (
            category === 'preferences' ||
            category === 'statistics' ||
            category === 'marketing'
          )
        })
      const hasAllConsents = requiredConsents.every((category) => {
        return payload.cookieConsent[category]
      })
      setHasConsent(hasAllConsents)
      setReady(true)
    },
    [consentCategory, consents]
  )

  useEffect(() => {
    if (!subscriptionsRef.current.length && consentGateEnabled) {
      const cookieConsent = {
        necessary: !!window.Cookiebot?.consent?.necessary,
        preferences: !!window.Cookiebot?.consent?.preferences,
        marketing: !!window.Cookiebot?.consent?.marketing,
        statistics: !!window.Cookiebot?.consent?.statistics,
      }
      checkConsent({ cookieConsent })

      const cookieConsentSetSubscription = subscribeTo<
        EventPayloadType[EventType.COOKIE_CONSENT_SET]
      >(
        EventType.COOKIE_CONSENT_SET,
        `CONSENT_GATE_${id}_COOKIE_CONSENT_SET_EVENT`
      )?.subscribe((payload) => {
        checkConsent(payload)
      })

      const cookieConsentDeclinedSubscription = subscribeTo<
        EventPayloadType[EventType.COOKIE_CONSENT_DECLINED]
      >(
        EventType.COOKIE_CONSENT_DECLINED,
        `CONSENT_GATE_${id}_COOKIE_CONSENT_DECLINED_EVENT`
      )?.subscribe((payload) => {
        checkConsent(payload)
      })

      const cookieConsentGrantedSubscription = subscribeTo<
        EventPayloadType[EventType.COOKIE_CONSENT_GRANTED]
      >(
        EventType.COOKIE_CONSENT_GRANTED,
        `CONSENT_GATE_${id}_COOKIE_CONSENT_GRANTED_EVENT`
      )?.subscribe((payload) => {
        checkConsent(payload)
      })

      subscriptionsRef.current = [
        cookieConsentSetSubscription,
        cookieConsentDeclinedSubscription,
        cookieConsentGrantedSubscription,
      ].filter((sub): sub is Subscription => sub !== undefined)
    }

    return () => {
      subscriptionsRef.current.forEach((sub) => sub.unsubscribe())
      subscriptionsRef.current = []
    }
  }, [checkConsent, consentGateEnabled, id, subscribeTo])

  return { consentGateEnabled, ready, hasConsent, text }
}
